import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Moon, Heart, Scale, RefreshCw, Unplug, Loader2, TrendingUp, Thermometer, Droplets, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";

interface DailyRollup {
  date: string;
  steps_total: number | null;
  sleep_minutes: number | null;
  resting_hr_avg: number | null;
  hr_avg: number | null;
  weight_kg: number | null;
  walking_distance_m: number | null;
  last_synced_at: string | null;
  source_summary: string | null;
  canonical_source_steps: string | null;
  canonical_source_sleep: string | null;
  canonical_source_hr: string | null;
  canonical_source_weight: string | null;
}

interface SyncState {
  connection_status: string;
  last_sync_at: string | null;
}

interface DeviceConnection {
  device: "oura" | "withings" | "fitbit";
  isConnected: boolean;
  isSyncing: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

function formatSourceLabel(source: string | null): string | null {
  if (!source) return null;
  const labels: Record<string, string> = {
    oura: "Oura",
    fitbit: "Fitbit",
    withings: "Withings",
    manual: "Manual",
  };
  return labels[source] || source;
}

export default function HealthSync() {
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const [rollups, setRollups] = useState<DailyRollup[]>([]);

  // Per-device loading states
  const [ouraState, setOuraState] = useState({ isConnected: false, isConnecting: false, isSyncing: false, isDisconnecting: false });
  const [withingsState, setWithingsState] = useState({ isConnected: false, isConnecting: false, isSyncing: false, isDisconnecting: false });
  const [fitbitState, setFitbitState] = useState({ isConnected: false, isConnecting: false, isSyncing: false, isDisconnecting: false });

  const anyConnected = ouraState.isConnected || withingsState.isConnected || fitbitState.isConnected;

  const fetchData = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    setIsAuthenticated(true);

    // Fetch sync state
    const { data: state } = await supabase
      .from("health_sync_state")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setSyncState(state as SyncState | null);

    // Check Oura connection
    const { data: ouraConn } = await supabase
      .from("oura_connections")
      .select("connection_status")
      .eq("user_id", user.id)
      .eq("connection_status", "connected")
      .maybeSingle();
    setOuraState(prev => ({ ...prev, isConnected: !!ouraConn }));

    // Check Withings connection
    const { data: withingsConn } = await supabase
      .from("withings_connections")
      .select("connection_status")
      .eq("user_id", user.id)
      .eq("connection_status", "connected")
      .maybeSingle();
    setWithingsState(prev => ({ ...prev, isConnected: !!withingsConn }));

    // Check Fitbit connection
    const { data: fitbitConn } = await supabase
      .from("fitbit_connections")
      .select("connection_status")
      .eq("user_id", user.id)
      .eq("connection_status", "connected")
      .maybeSingle();
    setFitbitState(prev => ({ ...prev, isConnected: !!fitbitConn }));

    // Fetch last 7 days of rollups
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const startDate = sevenDaysAgo.toISOString().split("T")[0];

    const { data: rollupsData } = await supabase
      .from("health_daily_rollups")
      .select("*")
      .eq("user_id", user.id)
      .gte("date", startDate)
      .order("date", { ascending: true });

    setRollups((rollupsData as DailyRollup[]) || []);
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle OAuth callback params
  useEffect(() => {
    if (searchParams.get("connected") === "true") {
      toast.success("Oura Ring connected successfully!");
      fetchData();
    }
    if (searchParams.get("withings_connected") === "true") {
      toast.success("Withings connected successfully!");
      fetchData();
    }
    if (searchParams.get("fitbit_connected") === "true") {
      toast.success("Fitbit connected successfully!");
      fetchData();
    }
    const error = searchParams.get("error");
    if (error) {
      const messages: Record<string, string> = {
        oauth_denied: "You declined the Oura connection request.",
        token_exchange_failed: "Failed to connect with Oura. Please try again.",
        withings_oauth_denied: "You declined the Withings connection request.",
        withings_token_failed: "Failed to connect with Withings. Please try again.",
        withings_missing_params: "Something went wrong with the Withings redirect.",
        withings_internal: "An unexpected Withings error occurred.",
        fitbit_oauth_denied: "You declined the Fitbit connection request.",
        fitbit_token_failed: "Failed to connect with Fitbit. Please try again.",
        fitbit_missing_params: "Something went wrong with the Fitbit redirect.",
        fitbit_internal: "An unexpected Fitbit error occurred.",
        fitbit_no_pending: "No pending Fitbit connection found. Please try again.",
      };
      toast.error(messages[error] || "Connection failed. Please try again.");
    }
  }, [searchParams, fetchData]);

  const handleConnect = async (device: "oura" | "withings" | "fitbit") => {
    const setter = device === "oura" ? setOuraState : device === "withings" ? setWithingsState : setFitbitState;
    setter(prev => ({ ...prev, isConnecting: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error("Please log in first."); return; }

      const res = await supabase.functions.invoke(`${device}-auth`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.error) throw new Error(res.error.message);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err: any) {
      toast.error(err.message || `Failed to start ${device} connection.`);
    } finally {
      setter(prev => ({ ...prev, isConnecting: false }));
    }
  };

  const handleSync = async (device: "oura" | "withings" | "fitbit") => {
    const setter = device === "oura" ? setOuraState : device === "withings" ? setWithingsState : setFitbitState;
    setter(prev => ({ ...prev, isSyncing: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error("Please log in first."); return; }

      const res = await supabase.functions.invoke(`${device}-sync`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.error) throw new Error(res.error.message);
      const label = device === "oura" ? "Oura" : device === "withings" ? "Withings" : "Fitbit";
      toast.success(`${label}: Synced ${res.data.days_synced} days, ${res.data.sleep_logs_inserted} sleep logs, ${res.data.vitals_inserted} vitals!`);
      await fetchData();
    } catch (err: any) {
      toast.error(err.message || "Sync failed.");
    } finally {
      setter(prev => ({ ...prev, isSyncing: false }));
    }
  };

  const handleDisconnect = async (device: "oura" | "withings" | "fitbit") => {
    const setter = device === "oura" ? setOuraState : device === "withings" ? setWithingsState : setFitbitState;
    setter(prev => ({ ...prev, isDisconnecting: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await supabase.functions.invoke(`${device}-disconnect`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: device === "oura" ? { delete_history: false } : undefined,
      });
      if (res.error) throw new Error(res.error.message);
      const label = device === "oura" ? "Oura Ring" : device === "withings" ? "Withings" : "Fitbit";
      toast.success(`${label} disconnected.`);
      await fetchData();
    } catch (err: any) {
      toast.error(err.message || "Failed to disconnect.");
    } finally {
      setter(prev => ({ ...prev, isDisconnecting: false }));
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const todayData = rollups.find((r) => r.date === today);
  const latestData = rollups.length > 0 ? rollups[rollups.length - 1] : null;
  const displayData = todayData || latestData;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Sign In Required</CardTitle>
            <CardDescription className="text-lg">
              Please sign in to your SeniorThrive account to connect your health devices.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Health Sync – SeniorThrive"
        description="Connect your Oura Ring or Withings devices to automatically sync health data with SeniorThrive."
        canonical="https://seniorthrive.com/health-sync"
        noindex
      />

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Health Sync</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {anyConnected
                ? "Your devices are connected. Here's your latest wellness data."
                : "Let your devices do the work – connect your health devices to automatically track your wellness."}
            </p>
          </div>

          {/* Device Connection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DeviceCard
              name="Oura Ring"
              description="Steps, sleep, heart rate, and weight from your Oura Ring."
              isConnected={ouraState.isConnected}
              isConnecting={ouraState.isConnecting}
              isSyncing={ouraState.isSyncing}
              isDisconnecting={ouraState.isDisconnecting}
              onConnect={() => handleConnect("oura")}
              onSync={() => handleSync("oura")}
              onDisconnect={() => handleDisconnect("oura")}
              metrics={[
                { icon: Activity, label: "Steps & Distance" },
                { icon: Moon, label: "Sleep Duration" },
                { icon: Heart, label: "Heart Rate" },
                { icon: Scale, label: "Weight" },
              ]}
            />
            <DeviceCard
              name="Withings"
              description="Weight, blood pressure, temperature, heart rate, SpO2, and sleep from Withings devices."
              isConnected={withingsState.isConnected}
              isConnecting={withingsState.isConnecting}
              isSyncing={withingsState.isSyncing}
              isDisconnecting={withingsState.isDisconnecting}
              onConnect={() => handleConnect("withings")}
              onSync={() => handleSync("withings")}
              onDisconnect={() => handleDisconnect("withings")}
              metrics={[
                { icon: Scale, label: "Weight" },
                { icon: Stethoscope, label: "Blood Pressure" },
                { icon: Thermometer, label: "Temperature" },
                { icon: Heart, label: "Heart Rate" },
                { icon: Droplets, label: "SpO2 (Oxygen)" },
                { icon: Moon, label: "Sleep" },
              ]}
              />
            <DeviceCard
              name="Fitbit"
              description="Steps, sleep, heart rate, weight, SpO2, and skin temperature from Fitbit devices."
              isConnected={fitbitState.isConnected}
              isConnecting={fitbitState.isConnecting}
              isSyncing={fitbitState.isSyncing}
              isDisconnecting={fitbitState.isDisconnecting}
              onConnect={() => handleConnect("fitbit")}
              onSync={() => handleSync("fitbit")}
              onDisconnect={() => handleDisconnect("fitbit")}
              metrics={[
                { icon: Activity, label: "Steps & Distance" },
                { icon: Moon, label: "Sleep" },
                { icon: Heart, label: "Heart Rate" },
                { icon: Scale, label: "Weight" },
                { icon: Droplets, label: "SpO2 (Oxygen)" },
                { icon: Thermometer, label: "Skin Temperature" },
              ]}
            />
          </div>

          {/* Data Dashboard (shown when any device is connected) */}
          {anyConnected && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard icon={Activity} label="Today's Steps" value={displayData?.steps_total} format={(v) => v.toLocaleString()} emptyText="No data" source={displayData?.canonical_source_steps} />
                <MetricCard icon={Moon} label="Last Night's Sleep" value={displayData?.sleep_minutes} format={(v) => `${(v / 60).toFixed(1)} hrs`} emptyText="No data" source={displayData?.canonical_source_sleep} />
                <MetricCard icon={Heart} label="Resting Heart Rate" value={displayData?.resting_hr_avg || displayData?.hr_avg} format={(v) => `${Math.round(v)} bpm`} emptyText="No data" source={displayData?.canonical_source_hr} />
                <MetricCard icon={Scale} label="Weight" value={displayData?.weight_kg} format={(v) => `${(v * 2.20462).toFixed(1)} lbs`} emptyText="No data" source={displayData?.canonical_source_weight} />
              </div>

              {syncState?.last_sync_at && (
                <p className="text-sm text-muted-foreground text-center">
                  Last synced: {new Date(syncState.last_sync_at).toLocaleString()}
                </p>
              )}

              {rollups.length > 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">7-Day Steps Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{ steps: { label: "Steps", color: "hsl(var(--primary))" } }}
                      className="h-[200px] w-full"
                    >
                      <LineChart data={rollups.map((r) => ({
                        date: new Date(r.date).toLocaleDateString("en-US", { weekday: "short" }),
                        steps: r.steps_total || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line type="monotone" dataKey="steps" stroke="var(--color-steps)" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DeviceCard({
  name,
  description,
  isConnected,
  isConnecting,
  isSyncing,
  isDisconnecting,
  onConnect,
  onSync,
  onDisconnect,
  metrics,
}: {
  name: string;
  description: string;
  isConnected: boolean;
  isConnecting: boolean;
  isSyncing: boolean;
  isDisconnecting: boolean;
  onConnect: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  metrics: Array<{ icon: React.ElementType; label: string }>;
}) {
  return (
    <Card className={isConnected ? "border-primary/30" : ""}>
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">{name}</CardTitle>
          {isConnected && (
            <span className="text-xs font-bold text-primary px-2 py-1 rounded-full">
              Connected
            </span>
          )}
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isConnected && (
          <div className="space-y-2">
            <p className="text-sm font-bold text-muted-foreground">What we'll sync:</p>
            <ul className="space-y-1.5">
              {metrics.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2">
          {!isConnected ? (
            <Button onClick={onConnect} disabled={isConnecting} variant="primary-cta" className="w-full">
              {isConnecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</> : `Connect ${name}`}
            </Button>
          ) : (
            <>
              <Button onClick={onSync} disabled={isSyncing} variant="primary-cta" size="sm">
                {isSyncing ? <><Loader2 className="h-4 w-4 animate-spin" /> Syncing…</> : <><RefreshCw className="h-4 w-4" /> Sync</>}
              </Button>
              <Button onClick={onDisconnect} disabled={isDisconnecting} variant="outline" size="sm">
                {isDisconnecting ? <><Loader2 className="h-4 w-4 animate-spin" /></> : <><Unplug className="h-4 w-4" /> Disconnect</>}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  format,
  emptyText,
  source,
}: {
  icon: React.ElementType;
  label: string;
  value: number | null | undefined;
  format: (v: number) => string;
  emptyText: string;
  source?: string | null;
}) {
  const sourceLabel = formatSourceLabel(source ?? null);

  return (
    <Card>
      <CardContent className="pt-6 text-center space-y-2">
        <Icon className="h-8 w-8 text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold text-foreground">
          {value != null ? format(value) : (
            <span className="text-muted-foreground text-lg font-normal">{emptyText}</span>
          )}
        </p>
        {sourceLabel && (
          <p className="text-xs text-muted-foreground/70">
            Source: {sourceLabel}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
